import React, { useEffect, useState } from 'react';
import Modal from 'react-responsive-modal';
import { PurpleButton } from '../../Components/Buttons/PurpleButton';
import { BlackButton } from '../../Components/Buttons/BlackButton';
import { useOnboarding } from './OnBoarding1';
import { useNavigate } from 'react-router-dom';
import { toast } from './Toast';
import { Logo } from '../../AssetComponents/Images';
import Logo1 from "../../assets/images/logoForQRcode.svg";
import keplrIcon from "../../assets/images/Keplr_icon_ver.1.3_2.svg";

const OnboardingModal = () => {
  const { 
    isOnboarding, 
    currentStep, 
    nextStep, 
    prevStep, 
    skipOnboarding, 
    completeOnboarding,
    setShowChatbot,
    setCurrentStep
  } = useOnboarding();
  const navigate = useNavigate();
  const [showChatbotMessage, setShowChatbotMessage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const effectiveStep = typeof currentStep === 'number' ? currentStep : 0;

  const steps = [
    {
      title: "Welcome to CodedEstate Rentals!",
      description: "Let us walk you through how to find your perfect stay",
      type: "welcome"
    },
    {
      title: "Plan Your Trip with Coded AI",
      description: "Your AI assistant is ready to help you find the perfect rental and answer any questions!",
      type: "ai",
      highlight: "chatbot"
    },
    {
      title: "Sort Your Listings",
      description: "You can order listings according to price ranges, ratings, and more to find exactly what you're looking for!",
      type: "sortings",
      highlight: "sortings"
    },
    {
      title: "Narrow Your Search",
      description: "Use filters to search by price, rating, type, and more",
      type: "filters",
      highlight: "filters"
    },
    {
      title: "Search for Your Perfect Stay",
      description: "Enter your destination, check-in/out dates, and number of guests for precise listings",
      type: "search",
      highlight: "search"
    },
    {
      title: "Navigate Your Listings",
      description: "Switch between different types of rental listings to find exactly what you need",
      type: "navigation",
      highlight: "navigation"
    },
    {
      title: "View Listings Instantly",
      description: "See available rentals with photos, ratings, and price info. Click 'View Details' for more",
      type: "listings",
      highlight: "listings"
    },
    {
      title: "Explore more across Coded Estate",
      description: "By clicking any of the options in the header you can seamlessly choose any of our modes and features across Coded Estate",
      type: "header",
      highlight: "header"
    },
    {
      title: "Connect Your Wallet",
      description: "Connect your Leap, Keplr, or MetaMask wallet to get started with secure bookings!",
      type: "wallet",
      highlight: "wallet"
    },
    {
      title: "Start Hosting - Verify Your Profile",
      description: "Complete your verification to start hosting properties on our platform",
      type: "verification",
      highlight: "verification"
    }
  ];

  const currentStepData = steps[currentStep];

  useEffect(() => {
    if (currentStep === 1) {
      // AI step - show chatbot automatically (but don't show modal here)
      setShowChatbot(true);
      
      // const timer = setTimeout(() => {
      //   toast.success("You can always reach me here if you need help!", {
      //     duration: 3000,
      //     position: 'bottom-right'
      //   });
      // }, 3000);

      return () => clearTimeout();
    }
  }, [currentStep, setShowChatbot]);

  // Prevent body scroll when onboarding modal is open
  useEffect(() => {
    if (isOnboarding && effectiveStep === 0) {
      document.body.classList.add('onboarding-modal-open');
    } else {
      document.body.classList.remove('onboarding-modal-open');
    }

    return () => {
      document.body.classList.remove('onboarding-modal-open');
    };
  }, [isOnboarding, effectiveStep]);

  const handleNext = () => {
    console.log('handleNext called - currentStep:', currentStep, 'steps.length:', steps.length);
    
    // Step 8 (wallet connection) - go to host dashboard for step 9 (sidebar modal)
    if (currentStep === 8) {
      // Instead of auto-navigating, show confirmation modal
      setPendingNavigation({
        path: '/onboarding/traveller?step=13',
        message: "Do you want to continue to the Travelers dashboard?"
      });
      setShowConfirmModal(true);
    } 
    // Steps 9+ are handled in HostDashboard
    else if (currentStep >= 9) {
      console.log('Steps 9+ should be handled in HostDashboard');
    } else {
      console.log('Calling nextStep()...');
      nextStep();
    }
  };

  const handleSkip = () => {
    skipOnboarding();
            navigate('/rent/all');
  };

  const handleBack = () => {
    if (currentStep === 10) {
      setPendingNavigation({
        path: '/onboarding/steps',
        message: "Would you like to return to the Rental tour?"
      });
      setShowConfirmModal(true);
    } else if (currentStep > 0) {
      prevStep();
    }
  };

  const handleConfirmNavigation = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation.path);
      setShowConfirmModal(false);
      setPendingNavigation(null);
      // Optionally update onboarding state here if needed
    }
  };

  const handleCancelNavigation = () => {
    setShowConfirmModal(false);
    setPendingNavigation(null);
  };

  const getWalletIcon = (walletType) => {
    switch (walletType) {
      case "leap":
        return "https://assets.leapwallet.io/logos/leap-cosmos-logo.svg";
      case "keplr":
        return keplrIcon;
      case "metamask":
      case "MetaMask":
        return "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg";
      case "bybit":
      case "Bybit":
        return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRqB80t8g90uzbolr1TPrZ6_T30jzCnvNok6g&s";
      default:
        return "";
    }
  };

  // Don't render anything if not onboarding
  if (!isOnboarding) {
    console.log('OnboardingModal - not rendering because isOnboarding=false');
    return null;
  }

  // Debug log to see what's happening
  console.log('OnboardingModal render - isOnboarding:', isOnboarding, 'currentStep:', currentStep, 'currentStepData:', currentStepData);

  return (
    <>
      {/* Custom Confirmation Modal - replaced react-responsive-modal for better CSS control */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
            <h2 className="text-xl font-semibold mb-4">{pendingNavigation?.message}</h2>
            <div className="flex gap-4 justify-center mt-6">
              <BlackButton text="Skip" onClick={() => {
                skipOnboarding();
                navigate('/rent/all');
              }} />
              <PurpleButton text="Yes, Continue" onClick={handleConfirmNavigation} />
            </div>
          </div>
        </div>
      )}

      {/* Background overlay - but not during step 2 (spotlight effect) */}
      {!(isOnboarding ) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      )}
      
      {/* Main welcome modal - only show on step 0 */}
      {effectiveStep === 0 && (
        <Modal
          open={true}
          center
          onClose={() => {}}
          showCloseIcon={false}
          classNames={{
            modal: "rounded-xl border-none shadow-2xl onboarding-highlight max-w-[95vw] sm:max-w-2xl md:max-w-3xl",
          }}
          styles={{
            modal: { zIndex: 9999, maxHeight: '95vh', width: '95vw' }
          }}
        >
          <div className="relative flex flex-col max-h-[95vh]">
            {/* Header with gradient background - Mobile optimized */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 p-3 sm:p-6 text-center text-white flex-shrink-0">
              <div className="flex justify-center mb-2 sm:mb-4">
                <div className="bg-white backdrop-blur-sm rounded-full p-2 sm:p-3">
                <img src={Logo1} alt="CodedEstate Logo" className="w-8 h-8 sm:w-12 sm:h-12" />
                </div>
              </div>
              <h1 className="text-lg sm:text-2xl font-bold mb-1">
                Welcome to CodedEstate!
              </h1>
              <p className="text-purple-100 text-xs sm:text-base">
                Your gateway to blockchain-powered rentals
              </p>
            </div>

            {/* Content section - Mobile optimized */}
            <div className="p-3 sm:p-6 bg-white flex-1 overflow-y-auto">
              <div className="text-center mb-3 sm:mb-6">
                <h2 className="text-sm sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">
                  Ready to explore the future of real estate?
                </h2>
                <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">
                  Let us walk you through how to find your perfect stay on our revolutionary platform 
                  Discover properties, connect your wallet, and book with confidence using blockchain technology
                </p>
              </div>

              {/* Feature highlights - Mobile optimized layout */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-6">
                <div className="text-center">
                  <div className="w-6 h-6 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                    <svg className="w-3 h-3 sm:w-5 sm:h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-medium">Crypto Payments</p>
                </div>
                <div className="text-center">
                  <div className="w-6 h-6 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                    <svg className="w-3 h-3 sm:w-5 sm:h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-medium">Secure Blockchain</p>
                </div>
                <div className="text-center col-span-2 sm:col-span-1">
                  <div className="w-6 h-6 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                    <svg className="w-3 h-3 sm:w-5 sm:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-medium">Global Properties</p>
                </div>
              </div>

              {/* Buttons - Mobile optimized */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-4">
                <BlackButton 
                  text="Skip Tour" 
                  onClick={handleSkip}
                  className="px-4 py-2 text-sm w-full sm:w-auto"
                />
                <PurpleButton 
                  text="Start Guided Tour"
                  onClick={handleNext}
                  className="px-4 py-2 text-sm w-full sm:w-auto"
                />
              </div>

              {/* Bottom note - Mobile optimized */}
              <div className="text-center mt-2 sm:mt-4 flex-shrink-0">
                <p className="text-xs text-gray-500">
                  This tour will take approximately 2 minutes to complete
                </p>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* AI instruction modal - now completely handled in InstructionRental.js */}
      {/* Step 1 modal removed - only handled in InstructionRental.js pointing to real chatbot */}

      {/* Header step - Step 7 with exact content from your specification */}
      {effectiveStep === 7 && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 w-96 bg-white rounded-lg shadow-lg p-4 border-4 border-purple-500 z-[99999] onboarding-highlight pointer-events-auto">
          <div className="text-lg font-semibold mb-2">Explore more across Coded Estate</div>
          <div className="text-gray-600 mb-4">By clicking any of the options in the header you can seamlessly choose any of our modes and features across Coded Estate</div>
          
          {/* Navigation items explanation */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3 p-2 bg-purple-50 rounded-lg">
              <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">R</div>
              <div className="text-sm">
                <div className="font-medium">Rent</div>
                <div className="text-xs text-gray-500">Browse listed properties for short term rental</div>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-2 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">B</div>
              <div className="text-sm">
                <div className="font-medium">Buy</div>
                <div className="text-xs text-gray-500">Tokenized real-world estate - coming soon</div>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-2 bg-green-50 rounded-lg">
              <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">T</div>
              <div className="text-sm">
                <div className="font-medium">Trade</div>
                <div className="text-xs text-gray-500">Speculate on the fluctuations of global real estate</div>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-2 bg-yellow-50 rounded-lg">
              <div className="w-6 h-6 bg-yellow-600 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">Y</div>
              <div className="text-sm">
                <div className="font-medium">YieldEstate</div>
                <div className="text-xs text-gray-500">Tokenized fractional ownership - coming soon</div>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
              <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">D</div>
              <div className="text-sm">
                <div className="font-medium">Dashboard</div>
                <div className="text-xs text-gray-500">Control all your activity from a centralized dashboard</div>
              </div>
            </div>
          </div>

          {/* Arrow pointing upward to header */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-purple-500"></div>
          
          <div className="flex gap-2">
            <BlackButton text="Back" onClick={handleBack} />
            <PurpleButton text="Next" onClick={handleNext} />
            <button 
              onClick={handleSkip}
              className="px-4 py-2 text-gray-500 hover:text-gray-700"
            >
              Skip Guide
            </button>
          </div>
        </div>
      )}

      {/* Wallet connection step - show on step 8 with responsive positioning */}
      {effectiveStep === 8 && !showConfirmModal && (
        <>
          {/* Desktop Version - Point upward to Connect Wallet button */}
          <div className="hidden lg:block fixed top-20 right-8 w-96 bg-white rounded-xl shadow-2xl p-6 border-4 border-purple-500 pointer-events-auto onboarding-highlight z-[99999]">
            <div className="text-xl font-bold mb-3 text-gray-800">Connect Your Wallet</div>
            <div className="text-gray-600 mb-6">Connect your Leap, Keplr, or MetaMask wallet to get started with secure bookings and payments!</div>
            
            {/* Wallet options display */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <img 
                  src={getWalletIcon("leap")} 
                  alt="Leap Wallet" 
                  className="w-10 h-10 rounded-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold" style={{display: 'none'}}>L</div>
                <div className="text-sm">
                  <div className="font-semibold text-gray-800">Leap Wallet</div>
                  <div className="text-gray-500">Cosmos ecosystem wallet</div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-xl border border-purple-200">
                <img 
                  src={getWalletIcon("keplr")} 
                  alt="Keplr Wallet" 
                  className="w-10 h-10 rounded-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold" style={{display: 'none'}}>K</div>
                <div className="text-sm">
                  <div className="font-semibold text-gray-800">Keplr Wallet</div>
                  <div className="text-gray-500">Secure Cosmos wallet</div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-xl border border-orange-200">
                <img 
                  src={getWalletIcon("metamask")} 
                  alt="MetaMask Wallet" 
                  className="w-10 h-10 rounded-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold" style={{display: 'none'}}>M</div>
                <div className="text-sm">
                  <div className="font-semibold text-gray-800">MetaMask</div>
                  <div className="text-gray-500">Ethereum & EVM compatible</div>
                </div>
              </div>
            </div>

            {/* Security notice */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="text-sm text-green-800">
                  <div className="font-semibold">Secure & Encrypted</div>
                  <div className="text-green-600">Your wallet stays safe with blockchain security</div>
                </div>
              </div>
            </div>

            {/* Arrow pointing upward to Connect Wallet button */}
            <div className="absolute -top-3 right-8 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-purple-500"></div>
            
            <div className="flex gap-3">
              <BlackButton text="Back" onClick={handleBack} />
              <PurpleButton text="Next" onClick={handleNext} />
              <button 
                onClick={handleSkip}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
              >
                Skip Guide
              </button>
            </div>
          </div>

          {/* Mobile Version - Point to mobile menu (three dots) */}
          <div className="lg:hidden fixed top-20 right-4 w-[calc(100vw-2rem)] max-w-80 bg-white rounded-xl shadow-2xl p-4 border-4 border-purple-500 pointer-events-auto onboarding-highlight z-[99999]">
            <div className="text-lg font-bold mb-3 text-gray-800">Connect Your Wallet</div>
            <div className="text-sm text-gray-600 mb-4">Tap the menu (three dots) at the top right to access wallet connection!</div>
            
            {/* Mobile wallet icons - smaller */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <img 
                  src={getWalletIcon("leap")} 
                  alt="Leap Wallet" 
                  className="w-8 h-8 rounded-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{display: 'none'}}>L</div>
                <div className="text-xs">
                  <div className="font-semibold text-gray-800">Leap Wallet</div>
                  <div className="text-gray-500">Cosmos ecosystem</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg border border-purple-200">
                <img 
                  src={getWalletIcon("keplr")} 
                  alt="Keplr Wallet" 
                  className="w-8 h-8 rounded-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{display: 'none'}}>K</div>
                <div className="text-xs">
                  <div className="font-semibold text-gray-800">Keplr Wallet</div>
                  <div className="text-gray-500">Secure Cosmos wallet</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
                <img 
                  src={getWalletIcon("metamask")} 
                  alt="MetaMask Wallet" 
                  className="w-8 h-8 rounded-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{display: 'none'}}>M</div>
                <div className="text-xs">
                  <div className="font-semibold text-gray-800">MetaMask</div>
                  <div className="text-gray-500">Ethereum & EVM</div>
                </div>
              </div>
            </div>

            {/* Arrow pointing upward to mobile menu (three dots) */}
            <div className="absolute -top-3 right-4 w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-purple-500"></div>
            
            <div className="onboarding-modal-buttons">
              <BlackButton text="Back" onClick={handleBack} />
              <PurpleButton text="Next" onClick={handleNext} />
              <button 
                onClick={handleSkip}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors text-sm"
              >
                Skip Guide
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default OnboardingModal;